import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import uniqid from "uniqid";
import { getImagesDB } from '@api';
import { enqueueResilientUpload, removeBlobEntry, uploadBaseForAttempt } from "@helpers/uploadResilience";
import { useModal } from "@helpers/hooks/useModal";
import {
  imageCountState,
} from "@atoms/priceCalc";
import {useRecoilState, useRecoilValue} from "recoil";
import "./style.css";
import OnboardScreens from "../../Shared/Modals/OnboardScreens";
import RestoreGalleryScreen from "../../Shared/Modals/RestoreGalleryScreen";
import {formUploadState, uploadPromptState, imagesBlobState} from "../../Recoil/Atoms";
import UploadPrompt from "../../Shared/Modals/UploadPrompt";
import UploadErrorSnackbar from "../../Shared/components/UploadErrorSnackbar";
import axios from "axios";
import clientLogger from '../../../utils/clientLogger';
import { downscaleForUpload } from '../../../utils/uploadDownscale';
import { getCartDiscountEvent } from '../../api/activePromoEvent';

const UploadImage = () => {
  const imagecount = useRecoilValue(imageCountState);
  const [uploadState,] = useRecoilState(formUploadState);
  const [isPrompt, setPrompt] = useRecoilState(uploadPromptState);
  const [, setImagesBlob] = useRecoilState(imagesBlobState);

  const [deskbody_opcy, setdeskbody_opcy] = useState();
  const [showSpinner, setshowSpinner] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);

  let navigate = useNavigate();
  const modal = useModal();
  const onboarding = localStorage.getItem('loginCompleted');

  useEffect(() => {
    // Check if the device is mobile
    const isMobile = window.innerWidth <= 767;

    if (isMobile) {
      // Disable scrolling on mobile
      document.body.classList.add('no-scroll');
    }

    return () => {
      if (isMobile) {
        // Re-enable scrolling when the component is unmounted
        document.body.classList.remove('no-scroll');
      }
    };
  }, []);
  
  useEffect(() => {
    setshowSpinner(true);
    window.scrollTo(0, 0);
    async function initiateData() {
      await getImagesDB();
      setshowSpinner(false);
    }
    initiateData();
  }, []);

  if(localStorage.getItem("dataExist") == 1){
    navigate("/review-your-images");
  }

  const opcy_desh_click = () => {
    setdeskbody_opcy("none");
  };

  // Function to generate a session ID
  // Function to generate a session ID via server API
  const generateSessionId = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/generate-session-id`);
      if (response.data && response.data.sessionId) {
        clientLogger.debug(`[CLIENT-DEBUG] Server generated session ID: ${response.data.sessionId}`);
        return response.data.sessionId;
      } else {
        throw new Error('Invalid response from session generation API');
      }
    } catch (error) {
      console.error('[CLIENT-DEBUG] Failed to generate session ID from server:', error.message);
      
      // Fallback to client-side generation with new format
      const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
      let fallbackId = 'b';
      for (let i = 0; i < 5; i++) {
        fallbackId += chars[Math.floor(Math.random() * chars.length)];
      }
      clientLogger.debug(`[CLIENT-DEBUG] Generated fallback session ID: ${fallbackId}`);
      return fallbackId;
    }
  };

  // Function to create a cart in the addtocart collection
  const createCart = async (uid, sessionId) => {
    try {
      const userEmail = localStorage.getItem("userEmail") || "";
      const fullName = localStorage.getItem("fullName") || "";
      const logIdentifier = userEmail ? `[${uid}-${userEmail}]` : `[${uid}]`;
      
      clientLogger.debug(`${logIdentifier} Creating cart with sessionId: ${sessionId}`);
      
      await fetch(`${process.env.REACT_APP_BASE_URL}/addtocart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: uid,
          sessionId: sessionId,
          userInfo: {
            fullName: fullName,
            email: userEmail
          },
          frameType: localStorage.getItem("frameSelected") || "classic",
          discountEvent: getCartDiscountEvent()
        }),
      });
      
      clientLogger.debug(`${logIdentifier} Cart created successfully with sessionId: ${sessionId}`);
    } catch (err) {
      console.error(`Error creating cart: ${err.message}`);
    }
  };

  const upload = async (e) => {
    if (e.type === "click") {
      openUploadMenu();
      return;
    } else {
      if (imagecount + e.target.files.length > 55) {
        alert("צרו קשר על מנת להעלות יותר מ55 תמונות");
        modal('hide', 'uploadOptions');
        return;
      }
      setshowSpinner(true);
      
      // Check if uniqueUserId already exists in localStorage
      let uid = localStorage.getItem("uniqueUserId");
      if (!uid) {
        // If not, create a new one
        uid = uniqid();
        localStorage.setItem("uniqueUserId", uid);
      }
      
      // Check if sessionId exists, create one if it doesn't
      let sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        // Generate a new sessionId
        sessionId = await generateSessionId();
        localStorage.setItem("sessionId", sessionId);
        clientLogger.debug(`Generated new sessionId: ${sessionId}`);
        
        // Create a cart in the addtocart collection
        await createCart(uid, sessionId);
      }
      
      const userEmail = localStorage.getItem("userEmail") || "";

      // Add email to log if available
      const logIdentifier = userEmail ? `[${uid}-${userEmail}]` : `[${uid}]`;
      clientLogger.debug(`${logIdentifier} Image upload started, count: ${e.target.files.length}`);
      // Unified resilient path — one job per file (bounded queue, stall watchdog,
      // endless network retry with connectivity wake-ups). We navigate to the
      // gallery IMMEDIATELY: every picked file shows as a white frame + spinner
      // and turns into the photo only when the server confirms it received it.
      // (The old batch path sent ALL files in one giant POST — one dropped byte
      // on cellular lost everything, silently.)
      const files = Array.from(e.target.files);
      if (!files.length) { setshowSpinner(false); return; }
      const startedAt = Date.now();
      const frametype = localStorage.getItem("frameSelected") || "classic";

      // Measure dimensions per file, but never gate the upload on decode success —
      // HEIC doesn't decode in the browser; the server converts and re-measures.
      const measured = await Promise.all(files.map((file) => new Promise((resolve) => {
        const img = document.createElement("img");
        const objUrl = URL.createObjectURL(file);
        img.onload = function () { resolve({ file, width: img.width, height: img.height, objUrl }); };
        img.onerror = function () { resolve({ file, width: 0, height: 0, objUrl }); };
        img.src = objUrl;
      })));

      // Natural order — first-picked-first (no jump-to-last anymore; the viewer
      // watches the start of the strip).
      const withIds = measured.map((m, i) => ({ ...m, fileUniqueId: uniqid(), fileUploadedAt: startedAt + i }));
      setImagesBlob((prev) => [
        ...prev,
        ...withIds.map(({ objUrl, fileUniqueId, fileUploadedAt }) => (
          { url: objUrl, id: fileUniqueId, uploadedAt: fileUploadedAt, uploadStatus: 'pending' }
        )),
      ]);
      withIds.forEach(({ file, width, height, fileUniqueId, fileUploadedAt }) => {

        (async () => {
          // Shrink the transfer before the network (shared 2000px short-edge cap);
          // dims must describe the file actually uploaded.
          let uploadFile = file;
          let upW = width;
          let upH = height;
          try {
            const prepped = await downscaleForUpload(file);
            uploadFile = (prepped && prepped.file) || file;
            if (prepped && prepped.w) { upW = prepped.w; upH = prepped.h; }
          } catch (err) { /* upload the original */ }

          enqueueResilientUpload({
            id: fileUniqueId,
            meta: {
              page: "upload",
              filename: file.name || "",
              sizeKB: Math.round((uploadFile.size || 0) / 1024),
              origKB: Math.round((file.size || 0) / 1024),
              sessionId,
              uid,
            },
            attempt: ({ signal, onUploadProgress, attemptNo }) => {
              if (localStorage.getItem("orderId")) localStorage.removeItem("orderId");
              const formdata = new FormData();
              formdata.append("image", uploadFile);
              formdata.append("uid", uid);
              formdata.append("email", userEmail);
              formdata.append("uniqueId", fileUniqueId);
              formdata.append("uploadedAtTime", fileUploadedAt);
              formdata.append("imagewidth", upW);
              formdata.append("imageheight", upH);
              formdata.append("imageext", 0);
              formdata.append("frametype", frametype);
              formdata.append("sessionId", sessionId);
              return axios.post(`${uploadBaseForAttempt(attemptNo)}/imageupload_single`, formdata, {
                headers: { "content-type": "multipart/form-data" },
                signal,
                onUploadProgress,
              });
            },
            onDone: async () => {
              localStorage.setItem('dataExist', 1);
              // The upload is ACKED — but this refresh can fail while the network
              // is still settling (upload succeeded during a 4G->WiFi flap).
              // Retry it, then ALWAYS clear the white frame: an acked upload must
              // never keep spinning.
              for (let i = 0; i < 3; i++) {
                try { await getImagesDB(); break; }
                catch (err) { await new Promise((r) => setTimeout(r, 2000 * (i + 1))); }
              }
              removeBlobEntry(fileUniqueId);
            },
            // Couldn't complete within the window — remove the white frame
            // (same outcome a refresh would give).
            onDrop: () => removeBlobEntry(fileUniqueId),
          });
        })();
      });

      // Track the pick for conversion attribution (uploads continue in background).
      const uploadCount = files.length;
      // GA4 - for Google Ads attribution
      if (window.gtag) {
        window.gtag('event', 'image_upload', {
          event_category: 'engagement',
          event_label: uploadCount === 1 ? 'single_upload' : 'multiple_upload',
          value: uploadCount,
          image_count: uploadCount
        });
      }

      // Meta Pixel
      if (window.fbq) {
        window.fbq('trackCustom', 'ImageUpload', {
          content_type: 'product',
          num_images: uploadCount
        });
      }

      // TikTok Pixel
      if (window.ttq) {
        window.ttq.track('ViewContent', {
          content_id: 'image_upload',
          content_type: 'product',
          content_name: 'Image Upload',
          quantity: uploadCount
        });
      }

      // Track add-to-cart event for image upload
      if (window.flashy && typeof window.flashy === 'function') {
        window.flashy('track', 'add_to_cart', {
          item_name: 'Custom Photo Frame',
          item_category: 'Frames',
          quantity: uploadCount,
          currency: 'ILS'
        });
      }

      setshowSpinner(false);
      navigate("/review-your-images");
    }
  };

  const openUploadMenu = () => {
    modal("open", "uploadOptions", {}, 400);
  };

  return (
    <>
      <UploadErrorSnackbar errors={uploadErrors} onDismiss={() => setUploadErrors([])} />
      {(uploadState || parseInt(onboarding) === 1) && (
        <UploadPrompt show={isPrompt}/>
      )}
      <div
        className="body_opcyyy"
        style={{ display: deskbody_opcy }}
        onClick={() => {
          opcy_desh_click();
        }}
      ></div>
      <div className={`desk-up`}>
        {!showSpinner && (
          <>
            <OnboardScreens />
            {/* DEV ONLY: ?previewRestore=1 renders the restore step with fixture data for design review */}
            {process.env.NODE_ENV === "development" && new URLSearchParams(window.location.search).get("previewRestore") === "1" && (
              <RestoreGalleryScreen
                data={{ imageCount: 55, totalImages: 60, capped: true, cartCount: 6, previews: [], maskedPhone: "054-•••-7015", maskedEmail: "hi•••@blends.co.il" }}
                firstName="אהרון"
                onSkip={() => {}}
                onAdopted={() => {}}
              />
            )}
            {(uploadState || parseInt(onboarding) === 1) && (
                <div className="form-upload">
                  <div className="form-area-upload">
                  <img src="https://www.facebook.com/tr?id=266939557094379&ev=OnboardingSuccess" height="1" width="1" alt=""/>
                  <h2>התחילו לעצב</h2>
                    <input
                        type="file"
                        accept="image/*"
                        className="file-input finputupload fpu"
                        multiple
                        name="file"
                        onChange={(e) => upload(e)}
                        onClick={(e) => { openUploadMenu(); e.preventDefault(); }}
                        style={{ zIndex: 10, height: "100%" }}
                    />
                    <div className="desk-upload-button-container">
                      <button className="desk-upload-button">
                      <img src="/assets/file/images/addimage.svg" alt="plus" />
                    </button>
                    </div>
                  </div>
                </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default UploadImage;
